import { useState, useEffect, useCallback, RefObject } from 'react';
import { isIOSDevice } from '../utils/uiUtils';

interface FullscreenApi {
  requestFullscreen: string;
  exitFullscreen: string;
  fullscreenElement: string;
  fullscreenEnabled: string;
  fullscreenchange: string;
  fullscreenerror: string;
}

// Browser variations for Fullscreen API
const fullscreenApis: FullscreenApi = (() => {
  if (typeof document === 'undefined') {
    // Handle server-side rendering or environments without document
    return {
      requestFullscreen: '', exitFullscreen: '', fullscreenElement: '',
      fullscreenEnabled: '', fullscreenchange: '', fullscreenerror: ''
    };
  }

  let browserApi: Partial<FullscreenApi> = {};
  if (typeof document.documentElement.requestFullscreen === 'function') {
    browserApi = {
      requestFullscreen: 'requestFullscreen',
      exitFullscreen: 'exitFullscreen',
      fullscreenElement: 'fullscreenElement',
      fullscreenEnabled: 'fullscreenEnabled',
      fullscreenchange: 'fullscreenchange',
      fullscreenerror: 'fullscreenerror',
    };
  } else if (typeof (document.documentElement as any).webkitRequestFullscreen === 'function') {
    browserApi = {
      requestFullscreen: 'webkitRequestFullscreen',
      exitFullscreen: 'webkitExitFullscreen',
      fullscreenElement: 'webkitFullscreenElement',
      fullscreenEnabled: 'webkitFullscreenEnabled',
      fullscreenchange: 'webkitfullscreenchange',
      fullscreenerror: 'webkitfullscreenerror',
    };
  } else if (typeof (document.documentElement as any).mozRequestFullScreen === 'function') {
    browserApi = {
      requestFullscreen: 'mozRequestFullScreen',
      exitFullscreen: 'mozCancelFullScreen',
      fullscreenElement: 'mozFullScreenElement',
      fullscreenEnabled: 'mozFullScreenEnabled',
      fullscreenchange: 'mozfullscreenchange',
      fullscreenerror: 'mozfullscreenerror',
    };
  } else if (typeof (document.documentElement as any).msRequestFullscreen === 'function') {
    browserApi = {
      requestFullscreen: 'msRequestFullscreen',
      exitFullscreen: 'msExitFullscreen',
      fullscreenElement: 'msFullscreenElement',
      fullscreenEnabled: 'msFullscreenEnabled',
      fullscreenchange: 'MSFullscreenChange',
      fullscreenerror: 'MSFullscreenError',
    };
  }
  // Ensure all keys are present even if API is not fully supported
  return {
    requestFullscreen: browserApi.requestFullscreen || '',
    exitFullscreen: browserApi.exitFullscreen || '',
    fullscreenElement: browserApi.fullscreenElement || '',
    fullscreenEnabled: browserApi.fullscreenEnabled || '',
    fullscreenchange: browserApi.fullscreenchange || '',
    fullscreenerror: browserApi.fullscreenerror || '',
  };
})();

export const useFullscreen = (elementRef: RefObject<HTMLElement>) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleFullscreenChange = useCallback(() => {
    if (typeof document !== 'undefined' && fullscreenApis.fullscreenElement) {
      const currentFullscreenElement = (document as any)[fullscreenApis.fullscreenElement];
      setIsFullscreen(!!currentFullscreenElement && currentFullscreenElement === elementRef.current);
    } else {
        setIsFullscreen(false);
    }
  }, [elementRef]);

  useEffect(() => {
    if (typeof document === 'undefined' || !fullscreenApis.fullscreenchange) {
      return; // SSR or unsupported API
    }

    const handleChange = () => handleFullscreenChange();
    const handleError = (event: Event) => {
      console.error('Fullscreen API error:', event);
      setIsFullscreen(false); // Reset state on error
    };

    document.addEventListener(fullscreenApis.fullscreenchange, handleChange);
    document.addEventListener(fullscreenApis.fullscreenerror, handleError);

    // Check initial state
    handleChange();

    return () => {
      document.removeEventListener(fullscreenApis.fullscreenchange, handleChange);
      document.removeEventListener(fullscreenApis.fullscreenerror, handleError);
    };
  }, [handleFullscreenChange]);

  const toggleFullscreen = useCallback(async () => {
    const element = elementRef.current;
    if (!element || typeof document === 'undefined') return;

    const requestMethodName = fullscreenApis.requestFullscreen;
    const exitMethodName = fullscreenApis.exitFullscreen;
    const currentFullscreenElementName = fullscreenApis.fullscreenElement;

    if (!requestMethodName || !exitMethodName || !currentFullscreenElementName) {
      console.warn("Fullscreen API method names not found.");
      return;
    }
    
    const requestMethod = (element as any)[requestMethodName];
    const exitMethod = (document as any)[exitMethodName];
    const currentFullscreenElement = (document as any)[currentFullscreenElementName];

    try {
      if (currentFullscreenElement === element) {
        // Exit fullscreen
        if (exitMethod && typeof exitMethod === 'function') {
          await exitMethod.call(document);
        } else {
            console.warn("Exit fullscreen method not supported or not a function.");
        }
      } else {
        // Enter fullscreen
        if (requestMethod && typeof requestMethod === 'function') {
          await requestMethod.call(element);
        } else {
            console.warn("Request fullscreen method not supported or not a function.");
        }
      }
    } catch (error) {
      console.error('Failed to toggle fullscreen:', error);
      // Attempt to reset state if something went wrong
       handleFullscreenChange();
    }
  }, [elementRef, handleFullscreenChange]);

  // MODIFIED isSupported logic
  const isSupported = (() => {
    if (typeof document === 'undefined') return false;
    
    // iOS Safari doesn't support the Fullscreen API properly
    if (isIOSDevice()) return false;
    
    const hasMethods = !!fullscreenApis.requestFullscreen && !!fullscreenApis.exitFullscreen;
    if (!hasMethods) return false;
    // Also check if fullscreen is enabled by the browser
    if (fullscreenApis.fullscreenEnabled) {
      return !!(document as any)[fullscreenApis.fullscreenEnabled];
    }
    // If fullscreenEnabled property itself isn't found, assume support if methods exist (older browsers)
    return true; 
  })();

  return { isFullscreen, toggleFullscreen, isSupported };
}; 